
import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { Skeleton } from '@/components/ui/skeleton';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { formatPhoneNumber } from '@/lib/utils';

type AuthContextType = {
  session: Session | null;
  user: User | null;
  loading: boolean;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType>({
  session: null,
  user: null,
  loading: true,
  signOut: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const isInitialRender = useRef(true);
  const navigationInProgress = useRef(false);
  const lastAuthenticatedPath = useRef<string | null>(null);
  
  // Set up auth state listener and check for existing session
  useEffect(() => {
    console.log('[AUTH] Initialize auth state - Component mounted', { path: location.pathname });
    let mounted = true;
    let subscription: { unsubscribe: () => void } | null = null;

    const setupAuth = async () => {
      try {
        // First set up the auth state listener
        console.log('[AUTH] Setting up auth state change listener');
        const { data } = supabase.auth.onAuthStateChange((event, currentSession) => {
          console.log('[AUTH] Auth state change event:', event, { 
            userId: currentSession?.user?.id, 
            path: location.pathname,
            hasSession: !!currentSession 
          });
          
          if (!mounted) {
            console.log('[AUTH] Component unmounted, ignoring auth state change');
            return;
          }
          
          setSession(currentSession);
          setUser(currentSession?.user ?? null);
          setLoading(false); // Make sure to set loading to false on auth state change
          
          if (currentSession?.user) {
            // Create or update profile non-blocking
            console.log('[AUTH] Current session has user, updating profile');
            setTimeout(() => {
              if (mounted) {
                createOrUpdateProfile(currentSession.user);
              }
            }, 0);
          } else {
            console.log('[AUTH] No user in current session');
          }
        });
        
        subscription = data.subscription;
        console.log('[AUTH] Auth state listener initialized');

        // Then check for existing session
        console.log('[AUTH] Checking for existing session');
        const { data: { session: initialSession }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('[AUTH] Error getting session:', error);
        }
        
        if (!mounted) {
          console.log('[AUTH] Component unmounted during session check');
          return;
        }
        
        console.log('[AUTH] Existing session check result:', initialSession ? 'Found session' : 'No session', {
          userId: initialSession?.user?.id,
          path: location.pathname
        });
        
        setSession(initialSession);
        setUser(initialSession?.user ?? null);
        
        if (initialSession?.user) {
          // Create or update profile non-blocking
          console.log('[AUTH] Initial session has user, updating profile');
          setTimeout(() => {
            if (mounted) {
              createOrUpdateProfile(initialSession.user);
            }
          }, 0);
        }

        setLoading(false);
      } catch (error) {
        console.error('[AUTH] Error initializing auth:', error);
        if (mounted) {
          setLoading(false);
        }
      }
    };

    setupAuth();

    return () => {
      console.log('[AUTH] Cleanup - unsubscribing and unmounting', { path: location.pathname });
      mounted = false;
      if (subscription) {
        subscription.unsubscribe();
      }
    };
  }, [location.pathname]);

  // Handle navigation based on auth state
  useEffect(() => {
    // Don't navigate on initial render, wait for auth to be established
    if (loading) {
      console.log('[AUTH-NAV] Auth still loading, skipping navigation', { path: location.pathname });
      return;
    }
    
    // Skip the first render to avoid unnecessary navigation
    if (isInitialRender.current) {
      console.log('[AUTH-NAV] Initial render, skipping navigation', { path: location.pathname });
      isInitialRender.current = false;
      return;
    }
    
    // Prevent navigation loops by tracking if a navigation is in progress
    if (navigationInProgress.current) {
      console.log('[AUTH-NAV] Navigation already in progress, skipping', { path: location.pathname });
      return;
    }

    const currentPath = location.pathname;
    const fullPath = location.pathname + location.search + location.hash;
    console.log('[AUTH-NAV] Checking navigation needs', { 
      path: currentPath,
      fullPath,
      isAuthenticated: !!user,
      userId: user?.id
    });
    
    // Define authentication-required and auth-excluded paths
    const authRequiredPaths = ['/app', '/profile', '/bookings', '/profile-setup', '/admin'];
    const authExcludedPaths = ['/auth'];
    
    const needsAuth = authRequiredPaths.includes(currentPath);
    const excludesAuth = authExcludedPaths.includes(currentPath);
    
    // Function to handle navigation
    const navigateIfNeeded = () => {
      if (user) {
        console.log('[AUTH-NAV] User is authenticated', { userId: user.id });
        
        // Store the current path if it's an authenticated path (not auth page)
        if (!excludesAuth && currentPath !== '/app') {
          lastAuthenticatedPath.current = fullPath;
          console.log('[AUTH-NAV] Storing last authenticated path:', lastAuthenticatedPath.current);
        }
        
        if (excludesAuth) {
          // User is logged in but on auth page, redirect to last path or app
          const redirectTo = lastAuthenticatedPath.current || '/app';
          console.log('[AUTH-NAV] Redirecting authenticated user from auth page to:', redirectTo, 
            { lastPath: lastAuthenticatedPath.current, currentPath });
          
          navigationInProgress.current = true;
          navigate(redirectTo, { replace: true });
          
          // Reset the flag after navigation
          setTimeout(() => {
            navigationInProgress.current = false;
            console.log('[AUTH-NAV] Navigation completed, reset flag');
          }, 100);
        } else {
          console.log('[AUTH-NAV] Authenticated user is on appropriate page', { path: currentPath });
        }
      } else {
        console.log('[AUTH-NAV] User is not authenticated');
        if (needsAuth) {
          // User is not logged in but on protected page, redirect to auth
          console.log('[AUTH-NAV] Redirecting unauthenticated user to auth page', { from: currentPath });
          
          // Store the attempted path to redirect back after login
          lastAuthenticatedPath.current = fullPath;
          console.log('[AUTH-NAV] Storing attempted path:', lastAuthenticatedPath.current);
          
          navigationInProgress.current = true;
          navigate('/auth', { replace: true });
          
          // Reset the flag after navigation
          setTimeout(() => {
            navigationInProgress.current = false;
            console.log('[AUTH-NAV] Navigation to auth completed, reset flag');
          }, 100);
        } else {
          console.log('[AUTH-NAV] Unauthenticated user is on appropriate page', { path: currentPath });
        }
      }
    };
    
    // Use a small delay to prevent multiple rapid redirects
    console.log('[AUTH-NAV] Setting timeout for navigation check');
    const navigationTimeout = setTimeout(navigateIfNeeded, 50);
    return () => {
      console.log('[AUTH-NAV] Clearing navigation timeout');
      clearTimeout(navigationTimeout);
    };
    
  }, [loading, user, location.pathname, location.hash, location.search, navigate]);

  // Helper function to create or update profile
  const createOrUpdateProfile = async (user: User) => {
    if (!user || !user.id) return;
    
    try {
      console.log('[AUTH-PROFILE] Creating or updating profile for user', user.id);
      
      // Format the phone number if available
      let formattedPhoneNumber = '';
      if (user.phone) {
        formattedPhoneNumber = formatPhoneNumber(user.phone);
      }
      
      // Check for existing profile by ID
      const { data: profileById, error: profileByIdError } = await supabase
        .from('profiles')
        .select('id, phone_number')
        .eq('id', user.id)
        .maybeSingle();
      
      if (profileByIdError && profileByIdError.code !== 'PGRST116') {
        console.error('[AUTH-PROFILE] Error fetching profile by ID:', profileByIdError);
        return;
      }
      
      // If profile exists, just return
      if (profileById) {
        console.log('[AUTH-PROFILE] Profile already exists for user', { userId: user.id });
        return;
      }
      
      // If no profile by ID but we have a phone number, try to find by phone
      if (formattedPhoneNumber) {
        console.log('[AUTH-PROFILE] Looking for profile by phone number', { 
          phone: formattedPhoneNumber,
          userId: user.id 
        });
        
        const { data: profileByPhone, error: profileByPhoneError } = await supabase
          .from('profiles')
          .select('id, phone_number')
          .eq('phone_number', formattedPhoneNumber)
          .maybeSingle();
        
        if (profileByPhoneError && profileByPhoneError.code !== 'PGRST116') {
          console.error('[AUTH-PROFILE] Error fetching profile by phone:', profileByPhoneError);
          return;
        }
        
        // If profile exists by phone, update it to link with current auth user ID
        if (profileByPhone) {
          console.log('[AUTH-PROFILE] Found profile by phone, updating ID', { 
            phone: formattedPhoneNumber,
            oldId: profileByPhone.id,
            newId: user.id 
          });
          
          const { error: updateError } = await supabase
            .from('profiles')
            .update({ id: user.id })
            .eq('phone_number', formattedPhoneNumber);
            
          if (updateError) {
            console.error('[AUTH-PROFILE] Error updating profile ID:', updateError);
          } else {
            console.log('[AUTH-PROFILE] Successfully updated profile ID');
          }
            
          return;
        }
      }
      
      // If no profile found at all, create a new one
      console.log('[AUTH-PROFILE] No profile found, creating new one', { userId: user.id });
      
      const { error: insertError } = await supabase
        .from('profiles')
        .insert({
          id: user.id,
          phone_number: formattedPhoneNumber || null
        });
        
      if (insertError) {
        console.error('[AUTH-PROFILE] Error creating profile:', insertError);
      } else {
        console.log('[AUTH-PROFILE] Successfully created new profile');
      }
        
    } catch (error) {
      console.error('[AUTH-PROFILE] Error creating/updating profile:', error);
    }
  };

  // Sign out function
  const signOut = async () => {
    try {
      console.log('[AUTH] Signing out user', { userId: user?.id });
      await supabase.auth.signOut();
      setSession(null);
      setUser(null);
      console.log('[AUTH] Sign out complete');
    } catch (error) {
      console.error('[AUTH] Error signing out:', error);
      toast.error('Error signing out');
    }
  };

  const value = {
    session,
    user,
    loading,
    signOut,
  };

  // Show loading indicator while checking auth
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="space-y-2">
          <Skeleton className="h-4 w-[250px]" />
          <Skeleton className="h-4 w-[200px]" />
        </div>
      </div>
    );
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
